import ReactGA from 'react-ga4';

// Helper function to check if we're in development mode
const isDevelopment = () => {
  return window.location.hostname === 'localhost' ||
         window.location.hostname === '127.0.0.1' ||
         process.env.NODE_ENV === 'development';
};

// Initialize Google Analytics
// Only runs in production, NOT on localhost
export const initGA = () => {
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;

  // Don't initialize if:
  // 1. No measurement ID is set
  // 2. Running on localhost
  // 3. Not in production environment
  if (!measurementId || window.location.hostname === 'localhost') {
    if (isDevelopment()) {
      console.log('[Analytics] Skipped: Running on localhost or no measurement ID');
    }
    return false;
  }

  try {
    ReactGA.initialize(measurementId, {
      gtagOptions: {
        send_page_view: false, // We'll send page views manually
      },
    });
    if (isDevelopment()) {
      console.log('[Analytics] Initialized with ID:', measurementId);
    }
    return true;
  } catch (error) {
    console.error('[Analytics] Initialization failed:', error);
    return false;
  }
};

// Track page views
export const trackPageView = (path, title) => {
  if (window.location.hostname === 'localhost') return;

  try {
    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title || document.title,
    });
    if (isDevelopment()) {
      console.log('[Analytics] Page view tracked:', path);
    }
  } catch (error) {
    console.error('[Analytics] Page view tracking failed:', error);
  }
};

// Track custom events
export const trackEvent = (category, action, label = null, value = null) => {
  if (window.location.hostname === 'localhost') {
    if (isDevelopment()) {
      console.log('[Analytics] Event (localhost):', { category, action, label, value });
    }
    return;
  }

  try {
    const eventData = {
      category,
      action,
    };

    // Only include label if it's provided
    if (label !== null) {
      eventData.label = label;
    }

    // Only include value if it's a number
    if (typeof value === 'number') {
      eventData.value = value;
    }

    ReactGA.event(eventData);
    if (isDevelopment()) {
      console.log('[Analytics] Event tracked:', eventData);
    }
  } catch (error) {
    console.error('[Analytics] Event tracking failed:', error);
  }
};

// Specific tracking functions for common actions

// Sign up modal tracking
export const trackSignUpModalOpened = () => {
  trackEvent('Sign Up', 'Modal Opened', 'Sign Up Modal');
};

export const trackSignUpModalClosed = () => {
  trackEvent('Sign Up', 'Modal Closed', 'Sign Up Modal');
};

export const trackSignUpSuccess = () => {
  trackEvent('Sign Up', 'Success', 'Email Submitted Successfully');
};

export const trackSignUpError = (errorType) => {
  trackEvent('Sign Up', 'Error', errorType);
};

export const trackSignUpSuccessViewed = () => {
  trackEvent('Sign Up', 'Success Message Viewed', 'Thank You Screen');
};

// Vendor assessment tracking
export const trackVendorAssessmentSubmit = () => {
  trackEvent('Vendor Assessment', 'Submit', 'Form Submitted Successfully');
};

export const trackVendorAssessmentError = (errorType) => {
  trackEvent('Vendor Assessment', 'Error', errorType);
};

// Navigation tracking
export const trackNavigationClick = (section) => {
  trackEvent('Navigation', 'Click', section);
};

// Privacy policy tracking
export const trackPrivacyPolicyClick = () => {
  trackEvent('Privacy', 'Link Clicked', 'Privacy Policy');
};

// Scroll depth tracking
export const trackScrollDepth = (percentage) => {
  trackEvent('Engagement', 'Scroll Depth', `${percentage}%`, percentage);
};

// External link tracking
export const trackExternalLink = (url) => {
  trackEvent('External Link', 'Click', url);
};

// Export all analytics functions as default
const analytics = {
  initGA,
  trackPageView,
  trackEvent,
  trackSignUpModalOpened,
  trackSignUpModalClosed,
  trackSignUpSuccess,
  trackSignUpError,
  trackSignUpSuccessViewed,
  trackVendorAssessmentSubmit,
  trackVendorAssessmentError,
  trackNavigationClick,
  trackPrivacyPolicyClick,
  trackScrollDepth,
  trackExternalLink,
};

export default analytics;
